import { Component, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { OrdersService } from 'src/app/services/orders/orders.service';
import { SparePartsService } from 'src/app/services/spareParts/spare-parts.service';
import { OrderOutModel } from 'src/app/models/order-out-model/order-out-model';
import { SparePartModel } from 'src/app/models/spare-part-model/spare-part-model';
import { AuthService } from 'src/app/services/auth/auth.service';

@Component({
    selector: 'app-history',
    templateUrl: './history.component.html',
    styleUrls: ['./history.component.css']
})
export class HistoryComponent implements OnInit, OnDestroy {
    @Output() searchFilterChanged = new EventEmitter<string>();
    @Output() dateFilterChanged = new EventEmitter<{ startDate?: Date, endDate?: Date }>();

    loading = false;
    spareParts: SparePartModel[] = [];
    orders: OrderOutModel[] = [];
    filteredOrders: OrderOutModel[] = [];
    showOrders = true;
    currentPage = 1;
    pageSize = 50;
    totalOrders = 0;
    totalPages = 1;
    searchTerm = '';
    dateFilterType = 'all';
    private previousDateFilterType = 'all';
    customStartDate?: Date | string;
    customEndDate?: Date | string;
    startDate?: Date;
    endDate?: Date;
    allOrdersLoaded = false;
    private isLoadingInProgress = false;
    private precalculatedSpareParts: SparePartModel[] = [];
    /** Si true, solo se muestran órdenes de los últimos 60 días; filtros aplican sobre ese subconjunto. */
    onlyLast60Days = false;
    private readonly searchDebounceMs = 400;
    private searchFilterSubject = new Subject<string>();
    private searchFilterSubscription?: Subscription;

    constructor(
        private orderService: OrdersService,
        private sparePartService: SparePartsService,
        private authService: AuthService,
        private router: Router
    ) {
    }

    ngOnInit(): void {
        if (this.authService.isLoggedIn() == false) {
            location.href = '/login';
        }
        this.setupSearchFilter();
        this.loadInitialOrders();
    }

    ngOnDestroy(): void {
        this.searchFilterSubscription?.unsubscribe();
    }

    private setupSearchFilter(): void {
        this.searchFilterSubscription = this.searchFilterSubject.pipe(
            debounceTime(this.searchDebounceMs),
            distinctUntilChanged(),
            tap((searchValue) => {
                this.searchTerm = searchValue;
                this.searchFilterChanged.emit(searchValue);
                this.currentPage = 1;
                this.loading = true;
            }),
            switchMap(() =>
                this.orderService.getPaginatedOrders(
                    this.currentPage,
                    this.pageSize,
                    this.searchTerm,
                    this.startDate,
                    this.endDate,
                    undefined,
                    undefined,
                    this.onlyLast60Days ? 60 : undefined
                ).pipe(
                    catchError((error) => {
                        this.handleInitialOrdersError(error);
                        return EMPTY;
                    })
                )
            )
        ).subscribe((response) => {
            this.handleInitialOrdersResponse(response);
        });
    }

    loadInitialOrders() {
        try {
            this.loading = true;

            this.orderService.getPaginatedOrders(
                this.currentPage,
                this.pageSize,
                this.searchTerm,
                this.startDate,
                this.endDate,
                undefined,
                undefined,
                this.onlyLast60Days ? 60 : undefined
            ).subscribe({
                next: (response) => this.handleInitialOrdersResponse(response),
                error: (error) => this.handleInitialOrdersError(error)
            });
        } catch (error) {
            console.error('Error en loadInitialOrders:', error);
            this.loading = false;
            this.orders = [];
            this.filteredOrders = [];
        }
    }

    private handleInitialOrdersResponse(response: any): void {
        // Importante: asignar directamente las órdenes filtradas, no todas las órdenes.
        this.orders = response.orders;
        this.filteredOrders = response.orders;
        this.totalOrders = response.totalCount;

        this.calculateTotalPages();
        this.allOrdersLoaded = this.orders.length >= this.totalOrders;
        this.loading = false;
    }

    private handleInitialOrdersError(error: any): void {
        console.error('Error al cargar órdenes:', error);
        this.loading = false;
        this.orders = [];
        this.filteredOrders = [];
        this.totalOrders = 0;
        this.allOrdersLoaded = true; // Para evitar intentos adicionales de carga

        if (error.error && typeof error.error === 'string') {
            console.error('Mensaje de error del servidor:', error.error);
        }
        if (error.message) {
            console.error('Mensaje de error:', error.message);
        }
    }

    loadMoreOrders() {
        if (this.loading || this.allOrdersLoaded) return;

        this.loading = true;

        // Incrementar la página actual
        this.currentPage++;

        // Usar directamente this.startDate y this.endDate que ya están configurados
        this.orderService.getPaginatedOrders(
            this.currentPage,
            this.pageSize,
            this.searchTerm,
            this.startDate,
            this.endDate,
            undefined,
            undefined,
            this.onlyLast60Days ? 60 : undefined
        ).subscribe({
            next: (response) => {
                // Agregar las nuevas órdenes a las existentes
                this.orders = [...this.orders, ...response.orders];
                this.filteredOrders = this.orders; // Mantener la lista filtrada actualizada

                // Verificar si ya se cargaron todas las órdenes
                this.allOrdersLoaded = this.orders.length >= response.totalCount;

                this.loading = false;
            },
            error: (error) => {
                console.error('Error al cargar más órdenes:', error);
                this.loading = false;

                // Revertir el incremento de página en caso de error
                this.currentPage--;
            }
        });
    }

    private normalizeString(str: any): string {
        if (str === null || str === undefined) return '';
        return str.toString()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "") // Quitar acentos
            .toLowerCase()
            .trim();
    }

    private createSearchRegex(term: string): RegExp {
        // Escapar caracteres especiales de regex
        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Crear una regex que busque el término como palabra completa o al inicio de una palabra
        return new RegExp(escapedTerm, 'i');
    }

    // Nueva función para dividir términos con guiones en componentes
    private splitHyphenatedTerms(term: string): string[] {
        return term.split(/-+/).filter(t => t.length > 0);
    }

    // El método applyFilters ya no es necesario porque ahora usamos el backend para filtrar
    // En su lugar, cuando se aplica un filtro, se llama a loadInitialOrders() que carga las órdenes
    // desde el backend con los filtros aplicados

    applyFilter(searchValue: string) {
        this.searchTerm = searchValue;
        this.searchFilterSubject.next(searchValue);
    }

    applySparePartsFilter() {
        // Si no hay repuestos precalculados, cargarlos primero
        if (this.precalculatedSpareParts.length === 0) {
            this.loadSpareParts();
            return;
        }

        // Si no hay término de búsqueda, mostrar todos los repuestos
        if (!this.searchTerm) {
            this.spareParts = [...this.precalculatedSpareParts];
            return;
        }

        // Dividir por espacios pero preservar términos con guiones
        const terms = this.searchTerm.match(/\S+(?:-\S+)*|\S+/g) || [];

        this.spareParts = this.precalculatedSpareParts.filter(part => {
            return terms.every(rawTerm => {
                const term = this.normalizeString(rawTerm);

                // Si el término contiene guión, buscar coincidencia PARCIAL en el estado
                // Esto permite buscar "EXPORTACION-ACTUAL" y encontrar "STOCK-EXPORTACION-ACTUAL-AUTOCOMERCIAL"
                if (term.includes('-')) {
                    const normalizedStatus = this.normalizeString(part.productStatus);
                    return normalizedStatus.includes(term);
                }

                // Para otros términos, buscar en todos los campos
                return [
                    this.normalizeString(part.sparePartName),
                    this.normalizeString(part.productStatus),
                    this.normalizeString(part.productCode),
                    this.normalizeString(part.taxNote),
                    this.normalizeString(part.ncm),
                    this.normalizeString(part.comments),
                    this.normalizeString(part.emergentComment || part.effectiveEmergentComment || ''),
                    this.normalizeString(part.expectedArrivalDate?.toString()),
                    this.normalizeString(part.orderNumber?.toString() || ''),
                    this.normalizeString(part.unitPrice?.toString()),
                    this.normalizeString(part.amount?.toString()),
                    this.normalizeString(part.valorDeVenta?.toString()),
                    this.normalizeString(part.codBr),
                    this.normalizeString(part.country),
                    this.normalizeString(part.shippingStatus),
                    this.normalizeString(part.shippingReceipt),
                ].some(field => field.includes(term));
            });
        });

    }

    applyDateFilter(dates: { startDate?: Date, endDate?: Date }) {
        this.startDate = dates.startDate ?? undefined;
        this.endDate = dates.endDate ?? undefined;
        this.dateFilterChanged.emit(dates);
        // Reiniciar a la primera página cuando se aplica un nuevo filtro
        this.currentPage = 1;
        this.loadInitialOrders();
    }

    // Método auxiliar para formatear fechas para inputs
    private formatDateForInput(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    loadSpareParts() {
        this.loading = true;

        // Crear mensaje de carga
        const loadingMessage = document.createElement('div');
        loadingMessage.className = 'fixed top-4 right-4 bg-white p-4 rounded-lg shadow-lg z-50 flex flex-col items-center';
        loadingMessage.innerHTML = `
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
        <p class="text-gray-700">Cargando repuestos...</p>
    `;
        document.body.appendChild(loadingMessage);

        // Limpiar datos actuales
        this.spareParts = [];
        this.precalculatedSpareParts = [];

        // Cargar repuestos desde el servicio
        this.sparePartService.getAllSpareParts().subscribe({
            next: (response) => {
                // Procesar y guardar los repuestos
                this.precalculatedSpareParts = response.map((part: SparePartModel) => ({
                    ...part,
                    orderNumber: part.orderNumber || 'N/A'
                }));

                // Ordenar por fecha
                this.precalculatedSpareParts.sort((a, b) => {
                    const dateA = new Date(a.expectedArrivalDate || 0);
                    const dateB = new Date(b.expectedArrivalDate || 0);
                    return dateB.getTime() - dateA.getTime();
                });

                // Asignar a la lista visible
                this.spareParts = [...this.precalculatedSpareParts];

                // Aplicar filtros si hay alguno
                if (this.searchTerm) {
                    this.applySparePartsFilter();
                }

                // Mostrar mensaje de finalización
                const completionMessage = document.createElement('div');
                completionMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded shadow-lg z-50';
                completionMessage.textContent = `Carga completada: ${this.spareParts.length} repuestos`;
                document.body.appendChild(completionMessage);
                setTimeout(() => completionMessage.remove(), 3000);

                this.loading = false;
                loadingMessage.remove();
            },
            error: (error) => {
                console.error('Error al cargar repuestos:', error);
                this.loading = false;
                this.spareParts = [];
                this.precalculatedSpareParts = [];

                // Mostrar mensaje de error
                const errorMessage = document.createElement('div');
                errorMessage.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded shadow-lg z-50';
                errorMessage.textContent = 'Error al cargar repuestos';
                document.body.appendChild(errorMessage);
                setTimeout(() => errorMessage.remove(), 3000);

                loadingMessage.remove();
            }
        });
    }

    toggleButtonOrderSparePart(buttonId: string): void {
        const wasShowingOrders = this.showOrders;
        this.showOrders = buttonId === 'profileButton';

        // Resetear paginación y filtros
        this.currentPage = 1;
        this.searchTerm = '';
        this.startDate = undefined;
        this.endDate = undefined;
        this.dateFilterType = 'all';

        // Si estamos cambiando a la vista de órdenes
        if (this.showOrders) {
            // Cargar órdenes como si fuera la primera vez
            this.loading = true;

            // // Emitir cambios en los filtros para actualizar la UI
            // this.searchFilterChanged.emit('');
            // this.dateFilterChanged.emit({startDate: undefined, endDate: undefined});

            // // Cargar la primera página de órdenes sin filtros
            // this.orderService.getPaginatedOrders(1, this.pageSize).subscribe({
            //   next: (response) => {
            //     this.orders = response.orders;
            //     this.filteredOrders = response.orders;
            //     this.totalOrders = response.totalCount;

            //     // Calcular el número total de páginas
            //     this.calculateTotalPages();

            //     this.loading = false;
            //   },
            //   error: (error) => {
            //     console.error('Error al cargar órdenes:', error);
            //     this.loading = false;
            //     this.orders = [];
            //     this.filteredOrders = [];
            //     this.totalOrders = 0;
            //   }
            // });


            //recargar pagina con f5
            window.location.reload();
        }
        // Si estamos cambiando a la vista de repuestos
        else {
            this.loadSpareParts();
        }
    }

    async loadAllOrders() {
        if (this.isLoadingInProgress) {
            return;
        }

        try {
            this.isLoadingInProgress = true;
            this.loading = true;
            this.allOrdersLoaded = false;
            this.currentPage = 1;
            this.orders = [];
            this.precalculatedSpareParts = []; // Limpiar caché de repuestos

            const batchSize = 50; // Aumentar significativamente el tamaño del lote
            let totalOrdersLoaded = 0;
            let keepLoading = true;

            // Almacenar todas las órdenes sin deduplicar
            let allOrders: OrderOutModel[] = [];

            while (keepLoading) {
                try {
                    const response = await this.orderService.getPaginatedOrders(
                        this.currentPage,
                        batchSize,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        this.onlyLast60Days ? 60 : undefined
                    ).toPromise();

                    if (response && response.orders && response.orders.length > 0) {
                        // Agregar todas las órdenes sin filtrar duplicados
                        allOrders = [...allOrders, ...response.orders];
                        totalOrdersLoaded += response.orders.length;

                        // Actualizar la lista de órdenes
                        this.orders = [...allOrders];
                        this.filteredOrders = [...this.orders];

                        // Mostrar progreso
                        const progressMessage = document.createElement('div');
                        progressMessage.className = 'fixed top-4 right-4 bg-blue-500 text-white px-6 py-3 rounded shadow-lg z-50';
                        progressMessage.textContent = `Cargando órdenes: ${totalOrdersLoaded} cargadas`;
                        document.body.appendChild(progressMessage);
                        setTimeout(() => progressMessage.remove(), 2000);

                        this.currentPage++;

                        // Si la respuesta tiene menos elementos que el tamaño del lote, hemos terminado
                        if (response.orders.length < batchSize) {
                            keepLoading = false;
                        }

                        // Pequeña pausa para no sobrecargar
                        await new Promise(resolve => setTimeout(resolve, 50));
                    } else {
                        keepLoading = false;
                    }
                } catch (error) {
                    console.error('Error cargando página de órdenes:', error);
                    keepLoading = false;
                }
            }

            this.allOrdersLoaded = true;

            // Contar total de repuestos
            const totalSpareParts = this.orders.reduce((total, order) =>
                total + (order.spareParts?.length || 0), 0);

            // Si estamos en la vista de repuestos, cargarlos
            if (!this.showOrders) {
                this.loadSpareParts();
            }

            // Mostrar mensaje de finalización
            const completionMessage = document.createElement('div');
            completionMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded shadow-lg z-50';
            completionMessage.textContent = `Carga completada: ${this.orders.length} órdenes con ${totalSpareParts} repuestos`;
            document.body.appendChild(completionMessage);
            setTimeout(() => completionMessage.remove(), 3000);

        } catch (error) {
            console.error('Error en loadAllOrders:', error);
            const errorMessage = document.createElement('div');
            errorMessage.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded shadow-lg z-50';
            errorMessage.textContent = 'Error al cargar las órdenes. Por favor, intente nuevamente.';
            document.body.appendChild(errorMessage);
            setTimeout(() => errorMessage.remove(), 5000);
        } finally {
            this.loading = false;
            this.isLoadingInProgress = false;
        }
    }

    // Método para ir a una página específica
    goToPage(page: number): void {
        // Validar que la página solicitada sea válida
        if (page < 1) page = 1;
        if (this.totalPages > 0 && page > this.totalPages) page = this.totalPages;

        // Actualizar la página actual
        this.currentPage = page;

        // Cargar la página con los filtros actuales
        this.loadPageWithFilters(page);
    }

    // Método para cargar una página específica con los filtros aplicados
    loadPageWithFilters(page: number): void {
        // Validar que la página solicitada sea válida
        if (page < 1) page = 1;
        if (this.totalPages > 0 && page > this.totalPages) page = this.totalPages;

        this.loading = true;
        this.currentPage = page;

        this.orderService.getPaginatedOrders(
            this.currentPage,
            this.pageSize,
            this.searchTerm,
            this.startDate,
            this.endDate,
            undefined,
            undefined,
            this.onlyLast60Days ? 60 : undefined
        ).subscribe({
            next: (response) => {
                // Actualizar las órdenes y el total
                this.orders = response.orders;
                this.filteredOrders = response.orders;
                this.totalOrders = response.totalCount;

                // Calcular el número total de páginas
                this.calculateTotalPages();

                // Verificar si ya se cargaron todas las órdenes
                this.allOrdersLoaded = this.orders.length >= this.totalOrders;

                this.loading = false;
            },
            error: (error) => {
                console.error('Error al cargar órdenes con filtros:', error);
                this.loading = false;
                this.orders = [];
                this.filteredOrders = [];
                this.totalOrders = 0;
                this.allOrdersLoaded = true; // Para evitar intentos adicionales de carga
            }
        });
    }

    // Método para obtener los números de página a mostrar
    getPageNumbers(): number[] {
        const pages: number[] = [];
        const maxPagesToShow = 5;

        if (this.totalPages <= maxPagesToShow) {
            // Si hay menos páginas que el máximo a mostrar, mostrar todas
            for (let i = 1; i <= this.totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Mostrar un subconjunto de páginas centrado en la página actual
            let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
            let endPage = startPage + maxPagesToShow - 1;

            if (endPage > this.totalPages) {
                endPage = this.totalPages;
                startPage = Math.max(1, endPage - maxPagesToShow + 1);
            }

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
        }

        return pages;
    }

    // Método para cambiar el tamaño de página
    onPageSizeChange(): void {
        this.currentPage = 1; // Volver a la primera página al cambiar el tamaño
        this.calculateTotalPages();
        this.loadPageWithFilters(this.currentPage);
    }

    // Método para calcular el total de páginas
    calculateTotalPages(): void {
        // Calcular el número total de páginas basado en el total de órdenes filtradas
        if (this.totalOrders <= 0) {
            this.totalPages = 1;
        } else {
            this.totalPages = Math.ceil(this.totalOrders / this.pageSize);
        }

    }

    onDateFilterChange(): void {
        // Al pasar a "Personalizado" desde otro filtro, limpiar fechas para no reutilizar valores viejos
        if (this.dateFilterType === 'custom' && this.previousDateFilterType !== 'custom') {
            this.customStartDate = undefined;
            this.customEndDate = undefined;
        }
        this.previousDateFilterType = this.dateFilterType;

        const isEmpty = (v: unknown) => v === undefined || v === null || v === '';

        // Personalizado sin ninguna fecha ingresada = mostrar TODAS las órdenes (sin filtro de fecha)
        if (this.dateFilterType === 'custom' && (isEmpty(this.customStartDate) && isEmpty(this.customEndDate))) {
            this.startDate = undefined;
            this.endDate = undefined;
            this.currentPage = 1;
            this.loadInitialOrders();
            return;
        }

        let dates: { startDate?: Date, endDate?: Date } = {};
        const today = new Date();

        switch (this.dateFilterType) {
            case 'today':
                dates.startDate = new Date(today.setHours(0, 0, 0, 0));
                dates.endDate = new Date(today.setHours(23, 59, 59, 999));
                break;
            case 'last7days':
                dates.endDate = new Date();
                dates.startDate = new Date(today.setDate(today.getDate() - 7));
                break;
            case 'last30days':
                dates.endDate = new Date();
                dates.startDate = new Date(today.setDate(today.getDate() - 30));
                break;
            case 'custom':
                if (this.customStartDate) {
                    const dStart = new Date(this.customStartDate as string);
                    dStart.setHours(0, 0, 0, 0);
                    dates.startDate = dStart;
                }
                if (this.customEndDate) {
                    const dEnd = new Date(this.customEndDate as string);
                    dEnd.setHours(23, 59, 59, 999);
                    dates.endDate = dEnd;
                } else {
                    dates.endDate = undefined;
                }
                break;
            default:
                dates = {};
                break;
        }

        this.applyDateFilter(dates);
    }

    goBack(): void {
        this.router.navigate(['/']);
    }
}
